import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Loader from '~/components/ui/Loader'
import CONFIG from '~/config'
import {
  EXPERIMENTS,
  GOOGLE_AUTH_TYPES,
} from '~/constants'
import { useExperiment } from '~/hooks/useExperiment'
import useGoogleAuthMethod from '~/hooks/useGoogleAuthMethod'
import { useSegmentEvents } from '~/hooks/useSegmentEvents'
import { ROUTES } from '~/pages/constants'
import {
  identifySegmentUser,
  MEMBER_REGISTERED,
  getSegmentUserContext,
  CONNECT_ACCOUNT_WITH_GOOGLE,
} from '~/services/segment'
import { useLazyFetchUserContextQuery } from '~/store/api'
import { clearRedirectUrl } from '~/store/modules/analytics/index'
import { setIsAuthorized } from '~/store/modules/auth'
import { setUserToken } from '~/utils'

const SignInWithGoogleButton = props => {
  const {
    promoCode,
    redirectRoute,
    type,
    width
  } = props

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { authMethod, authMethodApiResult } = useGoogleAuthMethod(type)

  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [fetchUserContext] = useLazyFetchUserContextQuery()

  const { enabled: isPrimeGuaranteeEnabled } = useExperiment(EXPERIMENTS.PRIME_3_MONTH_GUARANTEE, false)

  const [
    trackMemberRegistration,
    trackConnectAccountWithGoogle,
  ] = useSegmentEvents([
    MEMBER_REGISTERED,
    CONNECT_ACCOUNT_WITH_GOOGLE,
  ])

  const handleCredentialResponse = useCallback(async response => {
    setIsAuthenticating(true)

    const apiResult = await authMethod({
      jwtToken: response?.credential,
      promoCode,
      withCreditScoreGuarantee: isPrimeGuaranteeEnabled
    })
    const { data, error } = apiResult

    setIsAuthenticating(false)

    if (!error) {
      if (data.action === GOOGLE_AUTH_TYPES.SIGN_UP) {
        trackMemberRegistration({ promoCode, loginType: 'google' }, getSegmentUserContext(data.user))
      }
      setUserToken(data.jwtToken)
      dispatch(setIsAuthorized(true))
      identifySegmentUser(data.user)
      fetchUserContext()

      if (data.action === GOOGLE_AUTH_TYPES.SIGN_UP) {
        navigate(ROUTES.PRODUCT_TOURS.WELCOME)
      } else {
        navigate(redirectRoute || ROUTES.DASHBOARD)
      }

      if (data.action === GOOGLE_AUTH_TYPES.CONNECT) {
        trackConnectAccountWithGoogle()
      }

      dispatch(clearRedirectUrl())
    }

    if (error && error.data.errors?.[0]?.email) {
      window.google.accounts.id.revoke(error.data.errors[0].email)
    }
  }, [
    setIsAuthenticating,
    authMethod,
    trackMemberRegistration,
    dispatch,
    fetchUserContext,
    promoCode,
    isPrimeGuaranteeEnabled,
    navigate,
    redirectRoute,
    trackConnectAccountWithGoogle
  ])

  useEffect(() => {
    window.google?.accounts?.id.initialize({
      client_id: CONFIG.GOOGLE_OAUTH_CLIENT_ID,
      callback: handleCredentialResponse
    })
    window.google?.accounts?.id.renderButton(
      document.getElementById('google_oauth'),
      {
        theme: 'filled_blue',
        size: 'large',
        shape: 'rectangular',
        text: type === GOOGLE_AUTH_TYPES.SIGN_UP ? 'signup_with' : 'signin_with',
        width,
      }
    )
  }, [handleCredentialResponse, width, type])

  return (
    <>
      <div id="google_oauth" data-testid="stellar-google-oauth" className="flex justify-center mb-4" />
      {isAuthenticating ? (
        <Loader />
      ) : null}
      {authMethodApiResult.error ? (
        <p className="text-red text-sm pb-4">
          {authMethodApiResult.error?.data?.errors?.[0]?.message}
        </p>
      ) : null}
    </>
  )
}
SignInWithGoogleButton.propTypes = {
  promoCode: PropTypes.string,
  redirectRoute: PropTypes.string,
  type: PropTypes.oneOf([GOOGLE_AUTH_TYPES.SIGN_IN, GOOGLE_AUTH_TYPES.SIGN_UP, GOOGLE_AUTH_TYPES.CONNECT]),
  width: PropTypes.number,
}
SignInWithGoogleButton.defaultProps = {
  promoCode: '',
  redirectRoute: ROUTES.DASHBOARD,
  type: GOOGLE_AUTH_TYPES.SIGN_IN,
  width: 300
}

const Memoized = React.memo(SignInWithGoogleButton)
export default Memoized
